import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export class ApiError extends Error {
  status: number;
  data?: Record<string, unknown>;
  constructor(status: number, message: string, data?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'ApiError';
  }
}

async function getAuthHeader(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    const activeTenantId = localStorage.getItem('activeTenantId');
    
    if (!token) return {};
    
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`
    };

    if (activeTenantId) {
      headers['X-Tenant-ID'] = activeTenantId;
    }

    return headers;
  } catch {
    return {};
  }
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const authHeader = await getAuthHeader();
  
  const headers = {
    'Content-Type': 'application/json',
    ...authHeader,
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      // Clear session/redirect to login logic will be handled by the protected route 
      // when it detects session is invalid, but we could trigger a custom event here if needed
      console.warn('API returned 401 Unauthorized');
    }
    
    let errorMsg = response.statusText;
    let errorData: Record<string, unknown> | undefined;
    try {
      errorData = await response.json();
      if (typeof errorData?.error === 'string') errorMsg = errorData.error;
    } catch {
      // ignore JSON parse error
    }
    
    throw new ApiError(response.status, errorMsg, errorData);
  }

  // Handle empty responses
  if (response.status === 204) {
    return null;
  }

  return response.json();
}

export const api = {
  get: (endpoint: string, options?: RequestInit) => 
    fetchWithAuth(endpoint, { ...options, method: 'GET' }),
    
  post: (endpoint: string, data?: any, options?: RequestInit) => 
    fetchWithAuth(endpoint, { ...options, method: 'POST', body: JSON.stringify(data) }),
    
  put: (endpoint: string, data?: any, options?: RequestInit) => 
    fetchWithAuth(endpoint, { ...options, method: 'PUT', body: JSON.stringify(data) }),
    
  patch: (endpoint: string, data?: any, options?: RequestInit) => 
    fetchWithAuth(endpoint, { ...options, method: 'PATCH', body: JSON.stringify(data) }),
    
  delete: (endpoint: string, options?: RequestInit) => 
    fetchWithAuth(endpoint, { ...options, method: 'DELETE' }),
};
